// src/components/ReportLeadsScreen.jsx
// Relatório de Leads premium.
// Rota: /reports/leads
// Endpoint: GET /psy/report/lead?id=ID_USUARIO
// UI isolada em .lead-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Clock3,
  Info,
  Menu,
  Printer,
  RefreshCw,
  Target,
  Users,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/report-leads.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const ORIGIN_TONES = ["blue", "green", "orange", "purple", "pink", "teal"];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function buildQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (String(value).trim() === "") return;
    if (String(value).trim() === "todos") return;
    query.set(key, String(value));
  });
  return query.toString();
}

function getTimeBR(value) {
  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.detail || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function normalizeOrigins(origens = {}) {
  if (Array.isArray(origens)) {
    return origens
      .map((item) => ({
        nome: String(item?.origem || item?.nome || "Origem").trim(),
        total: safeNumber(item?.total),
        fechados: safeNumber(item?.fechados),
        taxaConversao: safeNumber(item?.taxaConversao),
      }))
      .filter((item) => item.nome && item.total >= 0)
      .sort((a, b) => b.total - a.total);
  }

  if (!origens || typeof origens !== "object") {
    return [];
  }

  return Object.entries(origens)
    .map(([nome, value]) => ({
      nome: String(nome || "Origem").trim(),
      total: safeNumber(value?.total ?? value),
      fechados: safeNumber(value?.fechados ?? 0),
      taxaConversao: safeNumber(value?.taxaConversao ?? 0),
    }))
    .filter((item) => item.nome && item.total >= 0)
    .sort((a, b) => b.total - a.total);
}

function normalizeMonthly(porMes = {}) {
  if (Array.isArray(porMes)) {
    return porMes.map((item) => ({
      mes: item?.mes || "",
      label: formatMonthLabel(item?.mes || ""),
      leads: safeNumber(item?.leads),
      fechados: safeNumber(item?.fechados),
      perdidos: safeNumber(item?.perdidos),
      taxaConversao: safeNumber(item?.taxaConversao),
    }));
  }

  const leads = porMes?.leads && typeof porMes.leads === "object" ? porMes.leads : {};
  const fechados = porMes?.fechados && typeof porMes.fechados === "object" ? porMes.fechados : {};

  const months = Array.from(new Set([...Object.keys(leads), ...Object.keys(fechados)])).sort();

  return months.map((mes) => ({
    mes,
    label: formatMonthLabel(mes),
    leads: safeNumber(leads?.[mes]),
    fechados: safeNumber(fechados?.[mes]),
  }));
}

function formatMonthLabel(value = "") {
  const [year, month] = String(value || "").split("-");
  const monthNumber = Number(month);

  if (!monthNumber || monthNumber < 1 || monthNumber > 12) {
    return String(value || "").slice(0, 7) || "Mês";
  }

  const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const suffix = year ? `/${String(year).slice(-2)}` : "";

  return `${months[monthNumber - 1]}${suffix}`;
}

function toneByIndex(index) {
  return ORIGIN_TONES[index % ORIGIN_TONES.length];
}

function toneColorVar(tone) {
  return `var(--lead-${tone})`;
}

function buildConic(entries = []) {
  const total = entries.reduce((sum, item) => sum + safeNumber(item.total), 0);

  if (!total) {
    return "conic-gradient(var(--lead-blue) 0 100%)";
  }

  let acc = 0;

  const stops = entries.map((item, index) => {
    const percent = (safeNumber(item.total) / total) * 100;
    const from = acc;
    acc += percent;

    return `${toneColorVar(toneByIndex(index))} ${from}% ${acc}%`;
  });

  return `conic-gradient(${stops.join(", ")})`;
}

function getLeadHealth(total, fechados) {
  const totalNumber = safeNumber(total);
  const closedNumber = safeNumber(fechados);
  const rate = totalNumber > 0 ? (closedNumber / totalNumber) * 100 : 0;

  if (totalNumber <= 0) {
    return {
      label: "Sem leads",
      tone: "neutral",
      icon: "i",
      rate,
      description: "Ainda não há leads suficientes para leitura do período.",
    };
  }

  if (rate >= 65) {
    return {
      label: "Conversão forte",
      tone: "good",
      icon: "good",
      rate,
      description: "O aproveitamento dos leads está em bom nível.",
    };
  }

  if (rate >= 35) {
    return {
      label: "Conversão moderada",
      tone: "warning",
      icon: "!",
      rate,
      description: "A conversão está razoável, mas ainda pode melhorar.",
    };
  }

  return {
    label: "Atenção comercial",
    tone: "bad",
    icon: "!",
    rate,
    description: "Há muitos leads não fechados no período.",
  };
}

function HealthStatusIcon({ tone }) {
  const Icon =
    tone === "good"
      ? CheckCircle2
      : tone === "bad"
        ? AlertCircle
        : tone === "warning"
          ? AlertTriangle
          : Info;

  return <Icon size={22} aria-hidden="true" />;
}

function KpiCard({
  icon: Icon,
  label,
  value,
  description,
  tone = "blue",
}) {
  return (
    <article className={`lead-kpi lead-kpi--${tone}`}>
      <div className="lead-kpi__icon" aria-hidden="true">
        <Icon size={19} />
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function OriginsDonut({ origins }) {
  const total = origins.reduce((sum, item) => sum + safeNumber(item.total), 0);

  return (
    <div className="lead-pie">
      <div className="lead-donut" role="img" aria-label="Distribuição de leads por origem">
        <div
          className="lead-donut__ring"
          style={{
            background: buildConic(origins),
          }}
        />

        <div className="lead-donut__hole">
          <strong>{total}</strong>
          <span>Total de leads</span>
        </div>
      </div>

      <div className="lead-legend">
        {origins.length ? (
          origins.map((origin, index) => {
            const percentage = total > 0 ? (safeNumber(origin.total) / total) * 100 : 0;
            const tone = toneByIndex(index);

            return (
              <div className="lead-legend__row" key={`${origin.nome}-${index}`}>
                <span className={`lead-dot lead-dot--${tone}`} aria-hidden="true" />
                <div>
                  <strong title={origin.nome}>{origin.nome}</strong>
                  <small>{percentBR(percentage)}%</small>
                </div>
                <span>{origin.total}</span>
              </div>
            );
          })
        ) : (
          <div className="lead-empty">Sem dados de origem.</div>
        )}
      </div>
    </div>
  );
}

function MonthlyBars({ rows = [] }) {
  if (!rows.length) {
    return (
      <div className="lead-chart-empty">
        <span><Target size={24} /></span>
        <strong>Sem evolução mensal.</strong>
        <small>Quando houver leads por mês, o gráfico aparecerá aqui.</small>
      </div>
    );
  }

  const visibleRows = rows.slice(-12);
  const max = Math.max(1, ...visibleRows.flatMap((item) => [item.leads, item.fechados]));

  return (
    <div className="lead-bars" role="img" aria-label="Leads e fechados por mês">
      <div className="lead-bars__y" aria-hidden="true">
        <span>{max}</span>
        <span>{Math.round(max / 2)}</span>
        <span>0</span>
      </div>

      <div className="lead-bars__plot">
        {visibleRows.map((row) => {
          const leadHeight = Math.max(6, Math.round((safeNumber(row.leads) / max) * 138));
          const closedHeight = Math.max(6, Math.round((safeNumber(row.fechados) / max) * 138));

          return (
            <div
              className="lead-bars__col"
              key={row.mes}
              title={`${row.label} — Leads: ${row.leads}, Fechados: ${row.fechados}`}
            >
              <div className="lead-bars__pair">
                <span className="lead-bars__bar is-leads" style={{ height: `${leadHeight}px` }} />
                <span className="lead-bars__bar is-closed" style={{ height: `${closedHeight}px` }} />
              </div>

              <span className="lead-bars__label">{row.label}</span>
            </div>
          );
        })}
      </div>

      <div className="lead-bars__hint">
        <span className="lead-hint lead-hint--leads">Leads</span>
        <span className="lead-hint lead-hint--closed">Fechados</span>
      </div>
    </div>
  );
}

function LeadsSkeleton() {
  return (
    <div className="lead-skeleton" aria-hidden="true">
      <div className="lead-skeleton__hero" />
      <div className="lead-skeleton__kpis">
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="lead-skeleton__grid">
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  message,
  onRetry,
  onBack,
  isError = false,
}) {
  return (
    <section
      className={`lead-state ${isError ? "lead-state--error" : ""}`}
    >
      <div className="lead-state__icon" aria-hidden="true">
        {isError ? (
          <AlertCircle size={24} />
        ) : (
          <Target size={24} />
        )}
      </div>

      <strong>{title}</strong>
      <span>{message}</span>

      <div className="lead-state__actions">
        <button
          className="lead-btn lead-btn--ghost-dark"
          type="button"
          onClick={onBack}
        >
          <ArrowLeft size={16} />
          Indicadores
        </button>

        <button
          className="lead-btn lead-btn--primary"
          type="button"
          onClick={onRetry}
        >
          <RefreshCw size={16} />
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function ReportLeadsScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [reportData, setReportData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [origemFilter, setOrigemFilter] = useState("");

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const loadReport = useCallback(async ({ signal, silent = false } = {}) => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!userId) {
      setErrorMessage("ID do usuário não encontrado. Faça login novamente.");
      setReportData(null);
      setLoading(false);
      return;
    }

    if (!token) {
      setErrorMessage("Token não encontrado. Faça login novamente.");
      setReportData(null);
      setLoading(false);
      return;
    }

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setErrorMessage("");

    try {
      const params = buildQuery({
        id: userId,
        dataInicio,
        dataFim,
        status: statusFilter,
        origem: origemFilter,
      });

      const response = await fetch(
        `${API_BASE}/psy/report/lead?${params}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        setErrorMessage("Sessão expirada. Faça login novamente.");
        setReportData(null);
        return;
      }

      if (!response.ok) {
        const message = await readApiError(response);
        setErrorMessage(`Erro ao carregar os dados (${response.status}). ${message}`);
        setReportData(null);
        return;
      }

      const data = await response.json().catch(() => null);

      setReportData(data || null);
      setLastUpdatedAt(new Date());
    } catch (error) {
      if (error?.name === "AbortError") return;

      console.error("Erro ao carregar relatório de leads:", error);
      setErrorMessage("Erro de conexão com a API.");
      setReportData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dataFim, dataInicio, origemFilter, statusFilter]);

  useEffect(() => {
    const controller = new AbortController();
    loadReport({ signal: controller.signal });

    return () => controller.abort();
  }, [loadReport]);

  const total = safeNumber(reportData?.total ?? reportData?.resumo?.total);
  const fechados = safeNumber(reportData?.fechados ?? reportData?.resumo?.fechados);
  const naoFechados = safeNumber(reportData?.naoFechados ?? reportData?.resumo?.naoFechados);
  const perdidos = safeNumber(reportData?.resumo?.perdidos);
  const tempoMedio = safeNumber(reportData?.tempoMedioFechamentoDias ?? reportData?.resumo?.tempoMedioFechamentoDias);

  const origins = useMemo(() => normalizeOrigins(reportData?.origens), [reportData]);
  const monthly = useMemo(() => normalizeMonthly(reportData?.porMes), [reportData]);

  const health = useMemo(() => getLeadHealth(total, fechados), [total, fechados]);

  const reload = () => loadReport({ silent: true });
  const clearManagementFilters = () => {
    setDataInicio("");
    setDataFim("");
    setStatusFilter("todos");
    setOrigemFilter("");
  };
  const goBack = () => navigate("/reports");
  const goClients = () => navigate("/clients");
  const goLogin = () => navigate("/login");
  const printReport = () => window.print();

  return (
    <div className="lead-page">
      <div className="lead-bg" aria-hidden="true">
        <span className="lead-bg__orb lead-bg__orb--one" />
        <span className="lead-bg__orb lead-bg__orb--two" />
        <span className="lead-bg__orb lead-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="lead-topbar">
        <button
          className="lead-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="lead-brand">
          <img src={Logo} alt="PsyAssist" className="lead-brand__logo" />
          <div className="lead-brand__text">
            <strong>Relatório de Leads</strong>
            <span>Resumo, origem e evolução • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="lead-top-actions">
          <button
            className="lead-btn lead-btn--ghost lead-btn--desktop"
            type="button"
            onClick={goBack}
          >
            <ArrowLeft size={16} />
            Indicadores
          </button>

          <button
            className="lead-btn lead-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(errorMessage) || !reportData}
          >
            <Printer size={16} />
            Imprimir
          </button>

          <button
            className="lead-btn lead-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "report-spin" : ""}
              aria-hidden="true"
            />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="lead-main">
        {loading ? (
          <LeadsSkeleton />
        ) : errorMessage ? (
          <EmptyState
            title="Não foi possível carregar os leads."
            message={errorMessage}
            onRetry={String(errorMessage).includes("Sessão") ? goLogin : reload}
            onBack={goBack}
            isError
          />
        ) : !reportData ? (
          <EmptyState
            title="Nenhum dado encontrado."
            message="Ainda não há leads suficientes para montar este relatório."
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="lead-hero">
              <div>
                <span className="lead-kicker">
                  <Target size={14} />
                  Aquisição e conversão
                </span>
                <h1>Leads</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  acompanhe o volume de leads, conversões, origem dos contatos e evolução
                  mensal do funil comercial.
                </p>
              </div>

              <aside className={`lead-hero-card lead-hero-card--${health.tone}`}>
                <div className="lead-hero-card__icon" aria-hidden="true">
                  <HealthStatusIcon tone={health.tone} />
                </div>

                <div>
                  <small>Status comercial</small>
                  <strong>{health.label}</strong>
                  <span>
                    Conversão: {percentBR(health.rate)}% • {health.description}
                  </span>
                </div>
              </aside>
            </section>

            <section className="lead-panel lead-management-filters">
              <div className="lead-panel__head">
                <div>
                  <span>Filtros de gestão</span>
                  <h2>Refine o funil</h2>
                  <p>Filtre leads por período, status e origem para entender melhor a conversão.</p>
                </div>
              </div>

              <div className="lead-filter-grid">
                <label className="lead-filter-field">
                  <span>Data início</span>
                  <input type="date" value={dataInicio} onChange={(event) => setDataInicio(event.target.value)} />
                </label>
                <label className="lead-filter-field">
                  <span>Data fim</span>
                  <input type="date" value={dataFim} onChange={(event) => setDataFim(event.target.value)} />
                </label>
                <label className="lead-filter-field">
                  <span>Status</span>
                  <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    <option value="todos">Todos</option>
                    <option value="Novo">Novo</option>
                    <option value="Em andamento">Em andamento</option>
                    <option value="Fechado">Fechado</option>
                    <option value="Perdido">Perdido</option>
                  </select>
                </label>
                <label className="lead-filter-field">
                  <span>Origem</span>
                  <input value={origemFilter} onChange={(event) => setOrigemFilter(event.target.value)} placeholder="Instagram, indicação..." />
                </label>
                <div className="lead-filter-actions">
                  <button className="lead-btn lead-btn--ghost-dark" type="button" onClick={clearManagementFilters}>Limpar</button>
                  <button className="lead-btn lead-btn--primary" type="button" onClick={reload} disabled={refreshing}>Aplicar</button>
                </div>
              </div>
            </section>

            <section className="lead-kpis">
              <KpiCard
                icon={Users}
                label="Total"
                value={total}
                description="Leads registrados"
                tone="blue"
              />

              <KpiCard
                icon={CheckCircle2}
                label="Fechados"
                value={fechados}
                description={`${percentBR(health.rate)}% de conversão`}
                tone="green"
              />

              <KpiCard
                icon={Ban}
                label="Não fechados"
                value={naoFechados}
                description="Oportunidades pendentes/perdidas"
                tone="red"
              />

              <KpiCard
                icon={Clock3}
                label="Tempo médio"
                value={`${tempoMedio} dias`}
                description="Tempo médio para fechamento"
                tone="amber"
              />

              <KpiCard
                icon={AlertTriangle}
                label="Perdidos"
                value={perdidos}
                description={`${percentBR(reportData?.resumo?.taxaPerda)}% de perda`}
                tone="red"
              />
            </section>

            <section className="lead-grid2">
              <div className="lead-panel">
                <div className="lead-panel__head">
                  <div>
                    <span>Origem</span>
                    <h2>Leads por origem</h2>
                    <p>Distribuição dos contatos por canal de entrada.</p>
                  </div>
                </div>

                <OriginsDonut origins={origins} />
              </div>

              <div className="lead-panel">
                <div className="lead-panel__head">
                  <div>
                    <span>Evolução</span>
                    <h2>Leads por mês</h2>
                    <p>Comparativo entre leads recebidos e fechados.</p>
                  </div>

                  {lastUpdatedAt ? (
                    <div className="lead-updated">
                      Atualizado às {getTimeBR(lastUpdatedAt)}
                    </div>
                  ) : null}
                </div>

                <MonthlyBars rows={monthly} />
              </div>
            </section>

            <section className="lead-bottom-actions">
              <button className="lead-btn lead-btn--ghost-dark" type="button" onClick={goBack}>
                <ArrowLeft size={16} />
                Central de indicadores
              </button>

              <button className="lead-btn lead-btn--primary" type="button" onClick={goClients}>
                <Users size={16} />
                Abrir pacientes
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
